import { useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { HiCalendar, HiChevronLeft, HiChevronRight } from 'react-icons/hi'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import { getLocalizedNews } from '../../data/newsData'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair } from '../../utils/seoUtils'
import './News.css'

const NEWS_PER_PAGE = 9

function News() {
  const { t, i18n } = useTranslation()
  const getLocalizedLink = useLocalizedLink()
  const [searchParams, setSearchParams] = useSearchParams()

  const allNewsItems = useMemo(() => getLocalizedNews(i18n.language), [i18n.language])

  const availableYears = useMemo(() => {
    const years = [...new Set(allNewsItems.map((item) => item.year))].filter(Boolean)
    return years.sort((a, b) => b - a)
  }, [allNewsItems])

  const yearFilter = useMemo(() => {
    const y = searchParams.get('year')
    if (!y || y === 'all') return 'all'
    const num = Number(y)
    return availableYears.includes(num) ? String(num) : 'all'
  }, [searchParams, availableYears])

  const filteredItems = useMemo(() => {
    if (yearFilter === 'all') return allNewsItems
    return allNewsItems.filter((item) => String(item.year) === yearFilter)
  }, [allNewsItems, yearFilter])

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / NEWS_PER_PAGE))
  const urlPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1)
  const currentPage = Math.min(urlPage, totalPages)

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * NEWS_PER_PAGE
    return filteredItems.slice(start, start + NEWS_PER_PAGE)
  }, [filteredItems, currentPage])

  const updateParams = (year, page) => {
    const params = new URLSearchParams()
    if (year && year !== 'all') params.set('year', year)
    if (page > 1) params.set('page', String(page))
    setSearchParams(params, { replace: true })
  }

  useEffect(() => {
    if (urlPage > totalPages) {
      updateParams(yearFilter, totalPages)
    }
  }, [urlPage, totalPages, yearFilter])

  const handleYearChange = (year) => {
    updateParams(year, 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const setPage = (page) => {
    const next = Math.min(Math.max(1, page), totalPages)
    updateParams(yearFilter, next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1])
    return [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b)
  }, [totalPages, currentPage])

  const showingFrom = filteredItems.length === 0 ? 0 : (currentPage - 1) * NEWS_PER_PAGE + 1
  const showingTo = Math.min(currentPage * NEWS_PER_PAGE, filteredItems.length)

  const breadcrumbItems = useMemo(
    () => seoBreadcrumbPair(t('common.home'), t('common.news'), 'news'),
    [t]
  )

  return (
    <div className="news-page">
      <SEO
        title={t('common.news')}
        description={t('news.description', 'Latest news from SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="news-hero">
          <h1>{t('common.news')}</h1>
          <p className="news-intro">
            {t(
              'news.intro',
              'Stay informed with the latest news, updates, and announcements from SUMA International and the martial arts community.'
            )}
          </p>
        </div>

        {availableYears.length > 0 && (
          <div className="news-toolbar">
            <div className="news-filter" role="group" aria-label={t('news.filterByYear', 'Filter by year')}>
              <button
                type="button"
                className={`filter-btn ${yearFilter === 'all' ? 'active' : ''}`}
                onClick={() => handleYearChange('all')}
              >
                {t('news.allYears', 'All years')}
              </button>
              {availableYears.map((year) => (
                <button
                  key={year}
                  type="button"
                  className={`filter-btn ${yearFilter === String(year) ? 'active' : ''}`}
                  onClick={() => handleYearChange(String(year))}
                >
                  {year}
                </button>
              ))}
            </div>

            {filteredItems.length > 0 && (
              <p className="news-results-meta">
                {t('news.showing', '{{from}}–{{to}} of {{total}}', {
                  from: showingFrom,
                  to: showingTo,
                  total: filteredItems.length,
                })}
              </p>
            )}
          </div>
        )}

        <div className="news-list">
          {paginatedItems.length > 0 ? (
            paginatedItems.map((item) => (
              <article key={item.id} className="news-card">
                {item.image && (
                  <div className="news-image-wrapper">
                    <img src={item.image} alt={item.title} className="news-image" style={{ objectFit: item.imageFit || 'cover' }} loading="lazy" />
                  </div>
                )}
                <div className="news-card-content">
                  <div className="news-date">
                    <HiCalendar className="date-icon" />
                    <span>{item.date}</span>
                  </div>
                  <h2>{item.title}</h2>
                  <p className="news-excerpt">{item.preview}</p>
                  <Link to={`${getLocalizedLink('/news')}/${item.id}`} className="news-link">
                    {t('news.readMore', 'Read more')} →
                  </Link>
                </div>
              </article>
            ))
          ) : (
            <div className="no-news">
              <p>
                {yearFilter !== 'all'
                  ? t('news.noNewsInYear', 'No news for the selected year.')
                  : t('news.noNews', 'News articles will be displayed here as they are published.')}
              </p>
            </div>
          )}
        </div>

        {totalPages > 1 && filteredItems.length > 0 && (
          <nav className="news-pagination" aria-label={t('news.pagination', 'News pages')}>
            <button
              type="button"
              className="pagination-btn"
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage <= 1}
              aria-label={t('news.prevPage', 'Previous page')}
            >
              <HiChevronLeft aria-hidden />
              <span>{t('news.prevPage', 'Previous')}</span>
            </button>

            <div className="pagination-pages">
              {pageNumbers.map((page, index) => {
                const prev = pageNumbers[index - 1]
                const showEllipsis = prev != null && page - prev > 1
                return (
                  <span key={page} className="pagination-page-wrap">
                    {showEllipsis && <span className="pagination-ellipsis">…</span>}
                    <button
                      type="button"
                      className={`pagination-page ${page === currentPage ? 'active' : ''}`}
                      onClick={() => setPage(page)}
                      aria-current={page === currentPage ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  </span>
                )
              })}
            </div>

            <button
              type="button"
              className="pagination-btn"
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              aria-label={t('news.nextPage', 'Next page')}
            >
              <span>{t('news.nextPage', 'Next')}</span>
              <HiChevronRight aria-hidden />
            </button>
          </nav>
        )}
      </div>
    </div>
  )
}

export default News
